import { useEffect, useMemo, useState } from "react";
import { EmptyState, Loading } from "@/components/common";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Inbox, Columns3, Check, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

const readCols = (k) => { try { return JSON.parse(localStorage.getItem(`ohc_cols_${k}`)); } catch { return null; } };

function ColumnPicker({ columns, visible, setVisible, storageKey }) {
  const toggle = (key) => {
    const next = visible.includes(key) ? visible.filter((x) => x !== key) : [...visible, key];
    setVisible(next); localStorage.setItem(`ohc_cols_${storageKey}`, JSON.stringify(next));
  };
  const reset = () => { localStorage.removeItem(`ohc_cols_${storageKey}`); setVisible(columns.filter((c) => !c.hidden).map((c) => c.key)); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm flex items-center gap-1.5 hover:border-[#4F46E5]/50 text-slate-600" data-testid={`${storageKey}-columns-btn`}>
          <Columns3 className="w-4 h-4" /> Columns
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 p-2 bg-white" data-testid={`${storageKey}-columns-menu`}>
        <div className="text-[11px] uppercase tracking-wider text-slate-400 px-2 pb-1">Show columns</div>
        <div className="max-h-72 overflow-y-auto">
          {columns.filter((c) => !c.fixed).map((c) => (
            <button key={c.key} onClick={() => toggle(c.key)} className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-slate-50" data-testid={`${storageKey}-col-${c.key}`}>
              <span className={`w-4 h-4 rounded border flex items-center justify-center ${visible.includes(c.key) ? "bg-[#4F46E5] border-[#4F46E5]" : "border-slate-300"}`}>{visible.includes(c.key) && <Check className="w-3 h-3 text-white" />}</span>
              {c.label}
            </button>
          ))}
        </div>
        <button onClick={reset} className="w-full text-xs text-[#4F46E5] mt-1 py-1 hover:bg-indigo-50 rounded" data-testid={`${storageKey}-columns-reset`}>Reset to default</button>
      </PopoverContent>
    </Popover>
  );
}

function Pager({ pager, testid, note }) {
  const { page, pageSize, total, setPage, setPageSize } = pager;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const btn = "h-8 w-8 rounded-md border border-slate-200 bg-white flex items-center justify-center disabled:opacity-40 hover:border-[#4F46E5]/50";
  return (
    <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-slate-500" data-testid={`${testid}-pager`}>
      <span>{total ? `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}` : "0 records"}</span>
      {note && <span className="text-amber-600" data-testid={`${testid}-loading-more`}>{note}</span>}
      <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="h-8 rounded-md border border-slate-200 bg-white px-2" data-testid={`${testid}-page-size`}>
        {[25, 50, 100, 200, 500].map((n) => <option key={n} value={n}>{n} / page</option>)}
      </select>
      <div className="ml-auto flex items-center gap-1">
        <button className={btn} disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`}><ChevronLeft className="w-4 h-4" /></button>
        <span className="px-2 font-semibold text-slate-700" data-testid={`${testid}-page`}>Page {page} / {pages}</span>
        <button className={btn} disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid={`${testid}-next`}><ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

const sortVal = (v) => (Array.isArray(v) ? v.join(", ") : v);
function sortRows(rows, key, dir) {
  if (!rows || !key) return rows;
  const m = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = sortVal(a[key]), y = sortVal(b[key]);
    const ex = x === null || x === undefined || x === "", ey = y === null || y === undefined || y === "";
    if (ex || ey) return ex === ey ? 0 : ex ? 1 : -1;
    if (typeof x === "number" && typeof y === "number") return (x - y) * m;
    const nx = Number(x), ny = Number(y);
    if (!Number.isNaN(nx) && !Number.isNaN(ny) && String(x).trim() !== "" && !/^\d{4}-\d{2}/.test(String(x))) return (nx - ny) * m;
    return String(x).localeCompare(String(y), undefined, { sensitivity: "base", numeric: true }) * m;
  });
}

// columns: [{ key, label, render?, hidden? (off by default), fixed? (always shown), sortKey?, sortable? }]; columnsKey enables the column picker
// paginate: client-side pages over the (already filtered) rows; onPageRows receives the rows of the visible page
// sortable: click a column header to sort asc/desc (over all rows, before pagination)
export default function DataTable({ columns, rows: inRows, loading, onRowClick, empty, testid, rowTestid, columnsKey, paginate, onPageRows, loadingMore, sortable }) {
  const [sort, setSort] = useState({ key: "", dir: "asc" });
  const allRows = useMemo(() => (sortable ? sortRows(inRows, sort.key, sort.dir) : inRows), [sortable, inRows, sort]);
  const toggleSort = (c) => {
    const k = c.sortKey || c.key;
    setSort((s) => (s.key !== k ? { key: k, dir: "asc" } : s.dir === "asc" ? { key: k, dir: "desc" } : { key: "", dir: "asc" }));
  };
  const canSort = (c) => sortable && c.sortable !== false && !String(c.key).startsWith("_") && c.label;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  useEffect(() => { setPage(1); }, [allRows]);
  const rows = useMemo(() => (paginate && allRows ? allRows.slice((page - 1) * pageSize, page * pageSize) : allRows), [paginate, allRows, page, pageSize]);
  useEffect(() => { if (onPageRows) onPageRows(rows || []); }, [rows]); // eslint-disable-line
  const pager = paginate && allRows ? { page, pageSize, total: allRows.length, setPage, setPageSize: (n) => { setPageSize(n); setPage(1); } } : null;
  const note = loadingMore ? "Loading remaining records…" : "";
  const [visible, setVisible] = useState(() => (columnsKey && readCols(columnsKey)) || columns.filter((c) => !c.hidden).map((c) => c.key));
  const cols = columnsKey ? columns.filter((c) => c.fixed || visible.includes(c.key)) : columns;
  let body;
  if (loading) body = <Loading />;
  else if (!rows || rows.length === 0) body = <div className="bg-white rounded-xl border border-slate-200"><EmptyState icon={Inbox} title={empty || "No records found"} /></div>;
  else body = (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]" data-testid={testid}>
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {cols.map((c) => {
                const k = c.sortKey || c.key;
                const on = sort.key === k;
                return (
                  <th key={c.key} className={`text-left font-semibold text-[10.5px] uppercase tracking-wider px-3 py-2 whitespace-nowrap ${on ? "text-[#4F46E5]" : "text-slate-500"} ${c.className || ""}`}>
                    {canSort(c) ? <button type="button" onClick={() => toggleSort(c)} className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-[#4F46E5]" data-testid={`${testid}-sort-${c.key}`}>{c.label}{on ? (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}</button> : c.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row, i) => (
              <tr key={row.id || i} data-testid={rowTestid ? `${rowTestid}-${i}` : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`${onRowClick ? "cursor-pointer hover:bg-indigo-50/40" : ""} transition-colors`}>
                {cols.map((c) => (
                  <td key={c.key} className={`px-3 py-2 text-slate-700 whitespace-nowrap ${c.tdClassName || ""}`}>{c.render ? c.render(row) : (row[c.key] ?? "—")}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
  if (!columnsKey) return pager ? <div>{body}<Pager pager={pager} testid={testid} note={note} /></div> : body;
  return (
    <div>
      <div className="flex items-center justify-end gap-2 mb-2">
        {rows && !loading && <span className="text-xs text-slate-400 mr-auto" data-testid={`${columnsKey}-count`}>{pager ? `${rows.length} shown on this page · ${pager.total} matching` : `${rows.length} records`}</span>}
        <ColumnPicker columns={columns} visible={visible} setVisible={setVisible} storageKey={columnsKey} />
      </div>
      {body}
      {pager && <Pager pager={pager} testid={testid} note={note} />}
    </div>
  );
}
